import * as types from '../mutation-types';
import { createActionHelpers } from 'vuex-loading';
import EventStock from '@js/franchise_component/franchise-inventory/stock/stock-event';
import Auth from '@js/plugins/auth';
import Unleash from '@js/plugins/unleash';

const { startLoading, endLoading } = createActionHelpers({
    moduleName: 'loading',
});

const state = {
    stock_movement: {
        outlet_id: null,
        reference_id: null,
        name: null,
        status: null,
        outlet_name: null,
        settled_at: null,
        settled_by: null,
        settled_name: null,
        created_by: null,
        created_name: null,
        supplier: {
            name: null,
            stocks: [],
        },
    },
    stock_movement_products: [],
};

const getters = {
    stockMovement() {
        return state.stock_movement;
    },

    hasFinalized: state => state.stock_movement.status == 2,

    completionStatus(state) {
        if (state.stock_movement.status == 2) {
            return 'Completed';
        }

        return 'In Progress';
    },

    stockMovementProducts: state =>
        state.stock_movement_products.map((p, i) => {
            p.row_index = i + 1;
            return p;
        }),

    findStockIndex: (state, getters) => stock_id => {
        return getters.stockMovementProducts.findIndex(
            s => s.stock_id == stock_id,
        );
    },

    receiveList: state => state.receiveList,

    totalVarieryCost: () => stock => {
        let total = stock.stock_movement_product_details.map(detail => {
            const cost = parseFloat(detail.adjustment_movement_cost);
            const quantity = parseFloat(Math.abs(detail.quantity));
            const total = cost * quantity;

            return parseFloat(total.toFixed(3));
        });

        let grandTotal = total.reduce((a, b) => a + b, 0);

        return parseFloat(grandTotal.toFixed(3));
    },

    totalSystemQty: (state, getters) => {
        return getters.stockMovementProducts
            .map(product => {
                return parseFloat(product.quantity_before);
            })
            .reduce((a, b) => a + b, 0);
    },

    totalReturnedQty: (state, getters) => {
        return getters.stockMovementProducts
            .map(product => {
                const validQty =
                    !_.isNaN(product.total_quantity) &&
                    !_.isNull(product.total_quantity) &&
                    !_.isEmpty(product.total_quantity);

                return validQty ? parseFloat(product.total_quantity) : 0;
            })
            .reduce((a, b) => a + b, 0);
    },

    totalUnitCost: (state, getters) => {
        return getters.stockMovementProducts
            .map(product => {
                const cost = parseFloat(product.supplier_price);
                const validCost = !_.isNaN(cost);
                return validCost ? cost : 0;
            })
            .reduce((a, c) => a + c, 0);
    },

    totalCost: (state, getters) => {
        return getters.stockMovementProducts
            .map(product => {
                const cost = parseFloat(product.supplier_price);
                const quantity = parseFloat(product.total_quantity);
                const validCost = !_.isNaN(cost) && !_.isNaN(quantity);
                return validCost ? cost * quantity : 0;
            })
            .reduce((a, c) => a + c, 0);
    },

    productCount: (state, getters) => {
        if (getters.stockMovementProducts.length !== 0) {
            return `Total: ${getters.stockMovementProducts.length}`;
        }

        return '';
    },

    findStockMovementProduct: (state, getters) => stock_id => {
        return getters.stockMovementProducts.find(s => s.stock_id == stock_id);
    },
};

const mutations = {
    [types.SET_STOCK_MOVEMENT](state, data) {
        state.stock_movement = data;
    },

    [types.SET_STOCK_MOVEMENT_ATTR](state, { key, value }) {
        state.stock_movement[key] = value;
    },

    [types.ADD_STOCK_MOVEMENT_PRODUCT](state, data) {
        state.stock_movement_products.push(data);
    },

    [types.UPDATE_STOCK_MOVEMENT_PRODUCT](state, { stockIndex, attr, value }) {
        state.stock_movement_products[stockIndex][attr] = value;
    },

    [types.REMOVE_STOCK_MOVEMENT_PRODUCT](state, stockIndex) {
        state.stock_movement_products.splice(stockIndex, 1);
    },

    [types.SAVE_STOCK_MOVEMENT_PRODUCT_DETAIL](state, payload) {
        const stock_movement_product = state.stock_movement_products.find(
            s => s.stock_id == payload.stock_id,
        );

        if (!_.isUndefined(stock_movement_product)) {
            stock_movement_product.stock_movement_product_details.push(payload);
        }
    },

    [types.REMOVE_STOCK_MOVEMENT_PRODUCT_DETAIL](state, payload) {
        const stock_movement_product = state.stock_movement_products.find(
            s => s.stock_id == payload.stock_id,
        );

        if (!_.isUndefined(stock_movement_product)) {
            const removeIndex = stock_movement_product.stock_movement_product_details.findIndex(
                s =>
                    s.quantity == payload.detail.quantity &&
                    payload.detail.stock_id &&
                    s.expired_at == payload.detail.expired_at,
            );

            stock_movement_product.stock_movement_product_details.splice(
                removeIndex,
                1,
            );

            stock_movement_product.stock_receive_list = [];
        }
    },

    merge(state, data) {
        if (Unleash.hasFeature('stock_tax_option')) {
            data.forEach(product => {
                if (!product.tax) {
                    product.tax = 'EXCLUSIVE';
                }
            });
        }

        state.stock_movement.stock_movement_products = data;
    },

    setCommitBy(state) {
        state.stock_movement.settled_by = Auth.user().id;
        state.stock_movement.settled_name = Auth.user().name;
    },

    setStockMovementProducts(state, data) {
        const shouldDefaultTax = Unleash.hasFeature('stock_tax_option');

        data.forEach(m => {
            m.stock_receive_list = [];
            if (shouldDefaultTax && !m.tax) m.tax = 'EXCLUSIVE';
        });

        state.stock_movement_products = data;
    },

    [types.SET_STOCK_RECEIVE_LIST](state, payload) {
        if (!_.isEmpty(payload)) {
            const stock_movement_product = state.stock_movement_products.find(
                s => s.stock_id == payload[0].stock_id,
            );

            if (!_.isUndefined(stock_movement_product)) {
                stock_movement_product.stock_receive_list = [];
                stock_movement_product.stock_receive_list.push(payload);
            }
        }
    },
};

const actions = {
    setStockReturnAttr({ commit }, { key, value }) {
        commit(types.SET_STOCK_MOVEMENT_ATTR, { key, value });
    },

    addStockMovementProduct({ commit }, { stock }) {
        const stock_movement_product = {
            name: stock.name,
            sku: stock.sku,
            code: stock.code,
            stock_id: stock.id,
            stock: stock,
            quantity_before: stock.quantity,
            total_quantity: null,
            stock_movement_product_details: [],
            stock_receive_list: [],
            supplier_price: null,
            total_cost: null,
            truncate_unit_cost: null,
            main_pricebook_unit_cost: stock.main_pricebook_unit_cost || 0,
            tax: Unleash.hasFeature('stock_tax_option') ? 'EXCLUSIVE' : null,
        };

        commit(types.ADD_STOCK_MOVEMENT_PRODUCT, stock_movement_product);
    },

    updateStockMovementProduct({ commit, getters }, { stock_id, attr, value }) {
        const stockIndex = getters.findStockIndex(stock_id);
        commit(types.UPDATE_STOCK_MOVEMENT_PRODUCT, {
            stockIndex,
            attr,
            value,
        });
    },

    removeStockMovementProduct({ commit, getters }, stock_id) {
        const stockIndex = getters.findStockIndex(stock_id);

        if (stockIndex >= 0) {
            commit(types.REMOVE_STOCK_MOVEMENT_PRODUCT, stockIndex);
            EventStock.$emit('remove-stock-id', stock_id);
        }
    },

    saveStockMovementProductDetail({ commit, state }, payload) {
        commit(types.SAVE_STOCK_MOVEMENT_PRODUCT_DETAIL, payload);
    },

    removeStockMovementProductDetail({ commit, state, dispatch }, payload) {
        commit(types.REMOVE_STOCK_MOVEMENT_PRODUCT_DETAIL, payload);

        let params = {
            outlet_id: payload.outlet_id,
            stock_id: payload.stock_id,
            supplier_id: payload.supplier_id,
        };

        // dispatch('getReceiveList', { params });
    },

    async createStockReturn({ commit, dispatch }, params) {
        const url = Laroute.route('inventory.stock-return.create');
        startLoading(dispatch, 'save-stock-return');

        try {
            const { data } = await axios.post(url, params);
            endLoading(dispatch, 'save-stock-return');

            return data;
        } catch (e) {
            endLoading(dispatch, 'save-stock-return');
            throw e;
        }
    },

    async getStockMovement({ commit, dispatch }, id) {
        const url = Laroute.route('inventory.stock-return.show', { id });
        startLoading(dispatch, 'fetch-stock-return');

        try {
            const { data } = await axios.get(url);
            endLoading(dispatch, 'fetch-stock-return');
            commit(types.SET_STOCK_MOVEMENT, data);

            return data;
        } catch (e) {
            endLoading(dispatch, 'fetch-stock-return');
            throw e;
        }
    },

    async getStockMovementProduct({ commit, dispatch }, id) {
        const url = Laroute.route('inventory.stock-return.product-list', {
            id,
        });
        startLoading(dispatch, 'fetch-product-list');

        try {
            const { data } = await axios.get(url);
            endLoading(dispatch, 'fetch-product-list');
            commit('setStockMovementProducts', data);

            data.map(d => {
                let params = {
                    outlet_id: state.stock_movement.outlet_id,
                    stock_id: d.stock_id,
                    supplier_id: state.stock_movement.supplier_id,
                };

                // dispatch('getReceiveList', { params });
            });

            return data;
        } catch (e) {
            endLoading(dispatch, 'fetch-product-list');
            throw e;
        }
    },

    async updateStockMovement({ commit, dispatch, state }) {
        const url = Laroute.route('inventory.stock-return.update');
        startLoading(dispatch, 'save-stock-return');
        commit('merge', state.stock_movement_products);

        try {
            const { data } = await axios.put(url, state.stock_movement);
            endLoading(dispatch, 'save-stock-return');
            commit(types.SET_STOCK_MOVEMENT, data.stock_movement);
            commit('setStockMovementProducts', data.stock_movement_products);
            EventStock.$emit('stock-list-reload');

            return data;
        } catch (e) {
            endLoading(dispatch, 'save-stock-return');
            throw e;
        }
    },

    async returned({ commit, dispatch, state }) {
        const url = Laroute.route('inventory.stock-return.returned');
        startLoading(dispatch, 'mark-returned');
        commit('merge', state.stock_movement_products);
        commit('setCommitBy');

        try {
            const { data } = await axios.post(url, state.stock_movement);
            endLoading(dispatch, 'mark-returned');
            commit(types.SET_STOCK_MOVEMENT, data.stock_movement);
            commit('setStockMovementProducts', data.stock_movement_products);

            return data;
        } catch (e) {
            endLoading(dispatch, 'mark-returned');
            throw e;
        }
    },

    async getReceiveList({ commit, dispatch }, params) {
        const url = Laroute.route('inventory.stock-return.receive-list');
        startLoading(dispatch, 'fetch-receive-list');

        try {
            const { data } = await axios.get(url, params);
            endLoading(dispatch, 'fetch-receive-list');
            commit(types.SET_STOCK_RECEIVE_LIST, data);

            return data;
        } catch (e) {
            endLoading(dispatch, 'fetch-receive-list');
            throw e;
        }
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    getters,
    actions,
};
